import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useAppSelector } from 'store/hooks';

import { getEventGates, getPartnerMembers } from './services';

/**
 * Resolves the gate the logged-in scanner is attributed to, for the "{gate} Gate" title on the verdict box.
 * Web scans are attributed server-side to the authenticated scanner's assigned gate; this mirrors what that
 * gate will be (read-only context, never a selector — Scanner/CLAUDE.md invariant 4).
 *
 * Gates store `scanner_id` = a partner-member `user_id`, which isn't guaranteed to equal the authenticated
 * user's ID, so we bridge through the roster (by id, else email) to the id the gate was assigned with —
 * best-effort: a scanner-role token that can't read the roster falls back to matching the auth id directly.
 *
 * Returns `gateName: null` while the queries are in flight OR if the gates fetch errors — so the caller hides
 * the title rather than flashing / showing a misleading "Default".
 */
export const useScannerGate = (eventId: string): { gateName: string | null } => {
	const { t } = useTranslation();
	const userId = useAppSelector((state) => state.auth.loggedUser?.ID);
	const userEmail = useAppSelector((state) => state.auth.loggedUser?.email);

	const gatesQuery = useQuery({
		queryKey: ['scanner-gates', eventId],
		queryFn: () => getEventGates(eventId),
		enabled: !!eventId,
	});

	const membersQuery = useQuery({
		queryKey: ['scanner-partner-members'],
		queryFn: getPartnerMembers,
	});

	const myScannerId = useMemo(() => {
		const member = membersQuery.data?.members?.find(
			(m) => m.user_id === userId || (!!userEmail && m.email === userEmail)
		);
		return member?.user_id ?? userId;
	}, [membersQuery.data, userId, userEmail]);

	return useMemo(() => {
		if (gatesQuery.isLoading || membersQuery.isLoading || gatesQuery.isError) return { gateName: null };
		const assigned = gatesQuery.data?.find((gate) => gate.assigned_scanners?.some((s) => s.scanner_id === myScannerId));
		return { gateName: assigned?.name ?? t('scanner-gate-default', 'Default') };
	}, [gatesQuery.isLoading, gatesQuery.isError, gatesQuery.data, membersQuery.isLoading, myScannerId, t]);
};
